import React, { useRef, useEffect, useState } from "react";
import { Stage, Layer, Image as KonvaImage, Text, Rect } from "react-konva";
import useImage from "use-image";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import jalaali from "jalaali-js";
import Swal from "sweetalert2";
import {
  Button,
  Box,
  Typography,
  Paper,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import {
  ListLetterHead,
  AddLetterHead,
  EditLetterHead,
  DeleteLetterHead,
  ListEmza,
  AddEmza,
  EditEmza,
  DeleteLetterEmza,
} from "../../services/letter";

// A4 dimensions in pixels at 96 DPI (approximately)
// A4: 210mm x 297mm = 794px x 1123px at 96 DPI
const A4_WIDTH = 595;
const A4_HEIGHT = 842;

// Coordinate transformation functions
// Normal data = canvas coordinates (what UI uses)
// Modified data = API coordinates (what API expects/receives)

/**
 * Convert normal (canvas) coordinates to modified (API) coordinates
 * Used when sending data to API (POST/PUT)
 */
const normalToModified = (canvasX, canvasY) => {
  const apiX = -70; // Fixed value based on API requirements
  const apiY = Math.round(A4_HEIGHT - canvasY - 715);
  return { x: apiX, y: apiY };
};

/**
 * Convert modified (API) coordinates to normal (canvas) coordinates
 * Used when receiving data from API (GET)
 */
const modifiedToNormal = (apiX, apiY) => {
  // Reverse of: apiY = A4_HEIGHT - canvasY - 715
  // So: canvasY = A4_HEIGHT - apiY - 715
  const canvasY = A4_HEIGHT - apiY - 715;
  // apiX is always -70, so use default x position (70)
  const canvasX = 30; // Default x position
  return { x: canvasX, y: canvasY };
};

const BackgroundImage = ({ image, showWhiteCanvas }) => {
  if (showWhiteCanvas || !image) {
    // Show white A4 canvas
    return (
      <Rect
        x={0}
        y={0}
        width={A4_WIDTH}
        height={A4_HEIGHT}
        fill="#FFFFFF"
        stroke="#CCCCCC"
        strokeWidth={1}
        listening={true}
      />
    );
  }

  // Use natural image dimensions
  return (
    <KonvaImage
      image={image}
      x={0}
      y={0}
      width={A4_WIDTH}
      height={A4_HEIGHT}
      listening={true}
    />
  );
};

// Editable Text Component
const EditableText = ({
  text,
  x,
  y,
  isSelected,
  onSelect,
  onDragEnd,
  fontSize = 16,
  fill = "#000000",
  fontFamily = "Vazirmatn",
}) => {
  const textRef = useRef(null);

  return (
    <Text  
      ref={textRef}
      text={text}
      x={x}
      y={y}
      fontSize={fontSize}
      fill={fill}
      fontFamily={fontFamily}
      draggable
      onClick={onSelect}
      onDragEnd={(e) => {
        onDragEnd({
          x: e.target.x(),
          y: e.target.y(),
        });
      }}
    />
  );
};

const Adjustment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentUserId = useSelector((state) => state.user.iduser);

  // Convert Jalali date 1404/9/30 to Gregorian ISO format
  const getDefaultDateISO = () => {
    try {
      const gregorian = jalaali.toGregorian(1404, 9, 30);
      const date = new Date(
        Date.UTC(
          gregorian.gy,
          gregorian.gm - 1, // JavaScript months are 0-indexed
          gregorian.gd,
          0,
          0,
          0,
          0
        )
      );
      return date.toISOString();
    } catch (error) {
      return new Date().toISOString();
    }
  };

  // Set default values if not provided in location.state
  const incomingFormData = location.state?.formData || {};
  const formData = {
    dateletter: incomingFormData.dateletter || getDefaultDateISO(),
    numletter: incomingFormData.numletter || "۱۲",
    isattach:
      incomingFormData.isattach !== undefined
        ? incomingFormData.isattach
        : false,
    isdate:
      incomingFormData.isdate !== undefined ? incomingFormData.isdate : false,
    isnumletter:
      incomingFormData.isnumletter !== undefined
        ? incomingFormData.isnumletter
        : false,
    attachmentNum: incomingFormData.attachmentNum || "دارد",
    ...incomingFormData,
  };
  const initialTemplateFile = location.state?.templateFile || null;
  const [templateFile, setTemplateFile] = useState(initialTemplateFile);
  const [existingTemplateId, setExistingTemplateId] = useState(null);
  const [existingTemplateIdStr, setExistingTemplateIdStr] = useState(null);
  const [existingTemplatePathimg, setExistingTemplatePathimg] = useState("0");
  const [templateName, setTemplateName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editSignatureDialogOpen, setEditSignatureDialogOpen] = useState(false);

  // Signature state
  const [signatureFile, setSignatureFile] = useState(null);
  const [existingSignatureId, setExistingSignatureId] = useState(null);
  const [existingSignatureIdStr, setExistingSignatureIdStr] = useState(null);
  const [existingSignaturePathimg, setExistingSignaturePathimg] = useState("0");
  const [signatureName, setSignatureName] = useState("");
  const [loadingSignature, setLoadingSignature] = useState(false);

  const stageRef = useRef(null);
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);
  const signatureFileInputRef = useRef(null);

  // Handle image URL - only set if there's a template file
  const [imageUrl, setImageUrl] = useState(() => {
    if (initialTemplateFile) {
      return URL.createObjectURL(initialTemplateFile);
    }
    return null; // No template, will show white canvas
  });
  const [image] = useImage(imageUrl);

  // Determine if we should show white canvas (no template file or image)
  const showWhiteCanvas = !templateFile && !image;

  // Load existing template on mount
  useEffect(() => {
    const loadExistingTemplate = async () => {
      try {
        setLoadingTemplate(true);
        const response = await ListLetterHead();
        const templates = response.data || [];
        if (templates.length > 0) {
          // Assume only one template exists
          const template = templates[0];
          const templateId = template.id || template.idsarbarg || 0;
          const templateIdStr = template.idsarbargstr || template.id || "";
          setExistingTemplateId(templateId);
          setExistingTemplateIdStr(templateIdStr);
          setTemplateName(template.name || "سربرگ ");

          // Store pathimg if available
          if (template.pathimg) {
            setExistingTemplatePathimg(template.pathimg);
          }

          // Set checkbox states from API response
          if (template.isdate !== undefined) {
            setIsdate(template.isdate);
          }
          if (template.isattach !== undefined) {
            setIsattach(template.isattach);
          }
          if (template.isnumletter !== undefined) {
            setIsnumletter(template.isnumletter);
          }

          // Convert modified (API) coordinates to normal (canvas) coordinates
          // GET from API -> modified data -> calculations -> normal data -> display in UI
          if (template.xdate !== undefined && template.ydate !== undefined) {
            const dateCoords = modifiedToNormal(
              template.xdate,
              template.ydate
            );
            setTextElements((prev) => ({
              ...prev,
              date: {
                ...prev.date,
                x: dateCoords.x,
                y: dateCoords.y,
              },
            }));
          }

          if (
            template.xnumletter !== undefined &&
            template.ynumletter !== undefined
          ) {
            const numCoords = modifiedToNormal(
              template.xnumletter,
              template.ynumletter
            );
            setTextElements((prev) => ({
              ...prev,
              numletter: {
                ...prev.numletter,
                x: numCoords.x,
                y: numCoords.y,
              },
            }));
          }

          if (template.xatach !== undefined && template.yatach !== undefined) {
            const attachCoords = modifiedToNormal(
              template.xatach,
              template.yatach
            );
            setTextElements((prev) => ({
              ...prev,
              attachment: {
                ...prev.attachment,
                x: attachCoords.x,
                y: attachCoords.y,
              },
            }));
          }

          // If template has an image URL, load it
          // Prioritize pathimg as the primary source for the image path
          const imageUrlFromApi =
            template.pathimg ||
            template.imageUrl ||
            template.url ||
            template.image;

          if (imageUrlFromApi && imageUrlFromApi !== "0") {
            // If it's a full URL, use it directly; otherwise construct the full URL
            if (imageUrlFromApi.startsWith("http")) {
              setImageUrl(imageUrlFromApi);
            } else {
              // Assuming the API base URL - adjust if needed
              setImageUrl(
                `https://api.copysa.ir${
                  imageUrlFromApi.startsWith("/") ? "" : "/"
                }${imageUrlFromApi}`
              );
            }
          }
        }
      } catch (error) {
        console.error("Error loading template:", error);
        // Don't show error, just continue without template
      } finally {
        setLoadingTemplate(false);
      }
    };

    // Only load if no initial template file is provided
    if (!initialTemplateFile) {
      loadExistingTemplate();
    }
  }, [initialTemplateFile]);

  // Load existing signature on mount
  useEffect(() => {
    const loadExistingSignature = async () => {
      try {
        setLoadingSignature(true);
        const response = await ListEmza();
        const signatures = response.data || [];
        if (signatures.length > 0) {
          // Assume only one signature exists
          const signature = signatures[0];
          const signatureId = signature.id || signature.idemza || 0;
          const signatureIdStr = signature.idemzastr || signature.id || "";
          setExistingSignatureId(signatureId);
          setExistingSignatureIdStr(signatureIdStr);
          setSignatureName(signature.name || "امضا");

          // Store pathimg if available
          if (signature.pathimg) {
            setExistingSignaturePathimg(signature.pathimg);
          }
        }
      } catch (error) {
        console.error("Error loading signature:", error);
        // Don't show error, just continue without signature
      } finally {
        setLoadingSignature(false);
      }
    };

    loadExistingSignature();
  }, []);

  // State for API checkboxes (isdate, isnumletter, isattach) - also control label display
  const [isdate, setIsdate] = useState(
    formData.isdate !== undefined ? formData.isdate : false
  );
  const [isnumletter, setIsnumletter] = useState(
    formData.isnumletter !== undefined ? formData.isnumletter : false
  );
  const [isattach, setIsattach] = useState(
    formData.isattach !== undefined ? formData.isattach : false
  );

  // Handle file selection (only stores file, doesn't call API)
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate that it's an image
      if (!file.type.startsWith("image/")) {
        Swal.fire({
          title: "خطا",
          text: "فقط فایل‌های تصویری قابل آپلود هستند",
          icon: "error",
          confirmButtonText: "باشه",
        });
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      // Cleanup old object URL if exists
      if (imageUrl && imageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(imageUrl);
      }
      setTemplateFile(file);
      const newImageUrl = URL.createObjectURL(file);
      setImageUrl(newImageUrl);
      // Reset initialization to center the new image
      setIsInitialized(false);
    }
  };

  // Handle template confirmation and API call
  const handleConfirmTemplate = async () => {
    // Validate that name and file are provided
    if (!templateName || templateName.trim() === "") {
      Swal.fire({
        title: "خطا",
        text: "لطفاً نام سربرگ را وارد کنید",
        icon: "error",
        confirmButtonText: "باشه",
      });
      return;
    }

    if (!templateFile && !existingTemplateId && !existingTemplateIdStr) {
      Swal.fire({
        title: "خطا",
        text: "لطفاً یک فایل سربرگ انتخاب کنید",
        icon: "error",
        confirmButtonText: "باشه",
      });
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();

      // Convert normal (canvas) coordinates to modified (API) coordinates
      // normal data -> calculations -> modified data -> POST to API
      const dateAPICoords = normalToModified(
        textElements.date.x,
        textElements.date.y
      );
      const numAPICoords = normalToModified(
        textElements.numletter.x,
        textElements.numletter.y
      );
      const attachAPICoords = normalToModified(
        textElements.attachment.x,
        textElements.attachment.y
      );

      // If template exists, edit it; otherwise, create new one
      if (existingTemplateId || existingTemplateIdStr) {
        // EditSarbarg format - include all new fields
        formData.append("idsarbarg", "0");
        formData.append("idsarbargstr", existingTemplateIdStr || "");
        formData.append("idUser", currentUserId ? String(currentUserId) : "0");
        formData.append("name", templateName || "");
        formData.append("pathimg", existingTemplatePathimg || "0");
        formData.append("lang", "fa");
        formData.append("isdate", isdate ? "true" : "false");
        formData.append("isattach", isattach ? "true" : "false");
        formData.append("isnumletter", isnumletter ? "true" : "false");
        formData.append("xdate", String(dateAPICoords.x));
        formData.append("ydate", String(dateAPICoords.y));
        formData.append("xatach", String(attachAPICoords.x));
        formData.append("yatach", String(attachAPICoords.y));
        formData.append("xnumletter", String(numAPICoords.x));
        formData.append("ynumletter", String(numAPICoords.y));
        if (templateFile) {
          formData.append("file", templateFile);
        }

        const editResponse = await EditLetterHead(formData);
        // Update pathimg from response if available
        if (editResponse.data?.pathimg) {
          setExistingTemplatePathimg(editResponse.data.pathimg);
        }
        Swal.fire({
          title: "موفق",
          text: "سربرگ  با موفقیت به‌روزرسانی شد",
          icon: "success",
          confirmButtonText: "باشه",
        });
      } else {
        // AddSarbarg format - include all new fields
        formData.append("idUser", currentUserId ? String(currentUserId) : "0");
        formData.append("name", templateName || "");
        formData.append("pathimg", "0");
        formData.append("lang", "fa");
        formData.append("isdate", isdate ? "true" : "false");
        formData.append("isattach", isattach ? "true" : "false");
        formData.append("isnumletter", isnumletter ? "true" : "false");
        formData.append("xdate", String(dateAPICoords.x));
        formData.append("ydate", String(dateAPICoords.y));
        formData.append("xatach", String(attachAPICoords.x));
        formData.append("yatach", String(attachAPICoords.y));
        formData.append("xnumletter", String(numAPICoords.x));
        formData.append("ynumletter", String(numAPICoords.y));
        formData.append("file", templateFile);

        const response = await AddLetterHead(formData);
        // Get the IDs from response
        const newId = response.data?.id || response.data?.idsarbarg || 0;
        const newIdStr = response.data?.idsarbargstr || response.data?.id || "";
        if (newId) {
          setExistingTemplateId(newId);
        }
        if (newIdStr) {
          setExistingTemplateIdStr(newIdStr);
        }
        // Store pathimg from response if available
        if (response.data?.pathimg) {
          setExistingTemplatePathimg(response.data.pathimg);
        }
        Swal.fire({
          title: "موفق",
          text: "سربرگ  با موفقیت آپلود شد",
          icon: "success",
          confirmButtonText: "باشه",
        });
      }
    } catch (error) {
      console.error("Error uploading template:", error);
      Swal.fire({
        title: "خطا",
        text: error.response?.data?.message || "مشکلی در آپلود سربرگ  رخ داد",
        icon: "error",
        confirmButtonText: "باشه",
      });
    } finally {
      setUploading(false);
    }
  };

  // Handle edit template name
  const handleEditTemplate = () => {
    setEditDialogOpen(true);
  };

  const handleSaveTemplateName = async () => {
    if (!existingTemplateId && !existingTemplateIdStr) {
      Swal.fire({
        title: "خطا",
        text: "ابتدا یک سربرگ  آپلود کنید",
        icon: "error",
        confirmButtonText: "باشه",
      });
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();

      // Convert normal (canvas) coordinates to modified (API) coordinates
      // normal data -> calculations -> modified data -> PUT to API
      const dateAPICoords = normalToModified(
        textElements.date.x,
        textElements.date.y
      );
      const numAPICoords = normalToModified(
        textElements.numletter.x,
        textElements.numletter.y
      );
      const attachAPICoords = normalToModified(
        textElements.attachment.x,
        textElements.attachment.y
      );

      // EditSarbarg format - include all new fields
      formData.append("idsarbarg", "0");
      formData.append("idsarbargstr", existingTemplateIdStr || "");
      formData.append("idUser", currentUserId ? String(currentUserId) : "0");
      formData.append("name", templateName || "");
      formData.append("pathimg", existingTemplatePathimg || "0");
      formData.append("lang", "fa");
      formData.append("isdate", isdate ? "true" : "false");
      formData.append("isattach", isattach ? "true" : "false");
      formData.append("isnumletter", isnumletter ? "true" : "false");
      formData.append("xdate", String(dateAPICoords.x));
      formData.append("ydate", String(dateAPICoords.y));
      formData.append("xatach", String(attachAPICoords.x));
      formData.append("yatach", String(attachAPICoords.y));
      formData.append("xnumletter", String(numAPICoords.x));
      formData.append("ynumletter", String(numAPICoords.y));
      // Note: file is optional when only updating name

      const editResponse = await EditLetterHead(formData);
      // Update pathimg from response if available
      if (editResponse.data?.pathimg) {
        setExistingTemplatePathimg(editResponse.data.pathimg);
      }
      setEditDialogOpen(false);
      Swal.fire({
        title: "موفق",
        text: "نام سربرگ  با موفقیت به‌روزرسانی شد",
        icon: "success",
        confirmButtonText: "باشه",
      });
    } catch (error) {
      console.error("Error updating template name:", error);
      Swal.fire({
        title: "خطا",
        text:
          error.response?.data?.message ||
          "مشکلی در به‌روزرسانی نام سربرگ  رخ داد",
        icon: "error",
        confirmButtonText: "باشه",
      });
    } finally {
      setUploading(false);
    }
  };

  // Handle template removal
  const handleRemoveTemplate = async () => {
    // Show confirmation dialog
    const result = await Swal.fire({
      title: "حذف سربرگ  ",
      text: "آیا از حذف این سربرگ  اطمینان دارید؟",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "حذف",
      cancelButtonText: "انصراف",
      confirmButtonColor: "#d32f2f",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setUploading(true);

      // Delete from backend if exists
      if (existingTemplateIdStr) {
        await DeleteLetterHead(existingTemplateIdStr);
      } else if (existingTemplateId) {
        await DeleteLetterHead(existingTemplateId);
      }

      // Cleanup object URL if exists
      if (imageUrl && imageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(imageUrl);
      }

      // Reset state
      setTemplateFile(null);
      setImageUrl(null);
      setExistingTemplateId(null);
      setExistingTemplateIdStr(null);
      setExistingTemplatePathimg("0");
      setTemplateName("");

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      // Reset initialization to center the white canvas
      setIsInitialized(false);

      Swal.fire({
        title: "موفق",
        text: "سربرگ  با موفقیت حذف شد",
        icon: "success",
        confirmButtonText: "باشه",
      });
    } catch (error) {
      console.error("Error deleting template:", error);
      Swal.fire({
        title: "خطا",
        text: error.response?.data?.message || "مشکلی در حذف سربرگ  رخ داد",
        icon: "error",
        confirmButtonText: "باشه",
      });
    } finally {
      setUploading(false);
    }
  };

  // Handle signature file upload and save to backend
  // Handle signature file selection (only stores file, doesn't call API)
  const handleSignatureFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate that it's an image
      if (!file.type.startsWith("image/")) {
        Swal.fire({
          title: "خطا",
          text: "فقط فایل‌های تصویری قابل آپلود هستند",
          icon: "error",
          confirmButtonText: "باشه",
        });
        // Reset file input
        if (signatureFileInputRef.current) {
          signatureFileInputRef.current.value = "";
        }
        return;
      }

      setSignatureFile(file);
    }
  };

  // Handle signature confirmation and API call
  const handleConfirmSignature = async () => {
    // Validate that name and file are provided
    if (!signatureName || signatureName.trim() === "") {
      Swal.fire({
        title: "خطا",
        text: "لطفاً نام امضا را وارد کنید",
        icon: "error",
        confirmButtonText: "باشه",
      });
      return;
    }

    if (!signatureFile && !existingSignatureId && !existingSignatureIdStr) {
      Swal.fire({
        title: "خطا",
        text: "لطفاً یک فایل امضا انتخاب کنید",
        icon: "error",
        confirmButtonText: "باشه",
      });
      return;
    }

    try {
      setUploadingSignature(true);
      const formData = new FormData();

      // If signature exists, edit it; otherwise, create new one
      if (existingSignatureId || existingSignatureIdStr) {
        // EditEmza format
        formData.append("idemza", "0");
        formData.append("idemzastr", existingSignatureIdStr || "");
        formData.append("name", signatureName || "");
        formData.append("pathimg", existingSignaturePathimg || "0");
        formData.append("iduser", "0");
        if (signatureFile) {
          formData.append("file", signatureFile);
        }

        const editResponse = await EditEmza(formData);
        // Update pathimg from response if available
        if (editResponse.data?.pathimg) {
          setExistingSignaturePathimg(editResponse.data.pathimg);
        }
        Swal.fire({
          title: "موفق",
          text: "امضا با موفقیت به‌روزرسانی شد",
          icon: "success",
          confirmButtonText: "باشه",
        });
      } else {
        // AddEmza format
        formData.append("idUser", "0");
        formData.append("name", signatureName || "");
        formData.append("pathimg", "0");
        formData.append("file", signatureFile);

        const response = await AddEmza(formData);
        // Get the IDs from response
        const newId = response.data?.id || response.data?.idemza || 0;
        const newIdStr = response.data?.idemzastr || response.data?.id || "";
        if (newId) {
          setExistingSignatureId(newId);
        }
        if (newIdStr) {
          setExistingSignatureIdStr(newIdStr);
        }
        // Store pathimg from response if available
        if (response.data?.pathimg) {
          setExistingSignaturePathimg(response.data.pathimg);
        }
        Swal.fire({
          title: "موفق",
          text: "امضا با موفقیت آپلود شد",
          icon: "success",
          confirmButtonText: "باشه",
        });
      }
    } catch (error) {
      console.error("Error uploading signature:", error);
      Swal.fire({
        title: "خطا",
        text: error.response?.data?.message || "مشکلی در آپلود امضا رخ داد",
        icon: "error",
        confirmButtonText: "باشه",
      });
    } finally {
      setUploadingSignature(false);
    }
  };

  // Handle edit signature name
  const handleEditSignature = () => {
    setEditSignatureDialogOpen(true);
  };

  const handleSaveSignatureName = async () => {
    if (!existingSignatureId && !existingSignatureIdStr) {
      Swal.fire({
        title: "خطا",
        text: "ابتدا یک امضا آپلود کنید",
        icon: "error",
        confirmButtonText: "باشه",
      });
      return;
    }

    try {
      setUploadingSignature(true);
      const formData = new FormData();

      // EditEmza format
      formData.append("idemza", "0");
      formData.append("idemzastr", existingSignatureIdStr || "");
      formData.append("name", signatureName || "");
      formData.append("pathimg", existingSignaturePathimg || "0");
      formData.append("iduser", "0");
      // Note: file is optional when only updating name

      const editResponse = await EditEmza(formData);
      // Update pathimg from response if available
      if (editResponse.data?.pathimg) {
        setExistingSignaturePathimg(editResponse.data.pathimg);
      }
      setEditSignatureDialogOpen(false);
      Swal.fire({
        title: "موفق",
        text: "نام امضا با موفقیت به‌روزرسانی شد",
        icon: "success",
        confirmButtonText: "باشه",
      });
    } catch (error) {
      console.error("Error updating signature name:", error);
      Swal.fire({
        title: "خطا",
        text:
          error.response?.data?.message ||
          "مشکلی در به‌روزرسانی نام امضا رخ داد",
        icon: "error",
        confirmButtonText: "باشه",
      });
    } finally {
      setUploadingSignature(false);
    }
  };

  // Handle signature removal
  const handleRemoveSignature = async () => {
    // Show confirmation dialog
    const result = await Swal.fire({
      title: "حذف امضا",
      text: "آیا از حذف این امضا اطمینان دارید؟",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "حذف",
      cancelButtonText: "انصراف",
      confirmButtonColor: "#d32f2f",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setUploadingSignature(true);

      // Delete from backend if exists
      if (existingSignatureIdStr) {
        await DeleteLetterEmza(existingSignatureIdStr);
      } else if (existingSignatureId) {
        await DeleteLetterEmza(existingSignatureId);
      }

      // Reset state
      setSignatureFile(null);
      setExistingSignatureId(null);
      setExistingSignatureIdStr(null);
      setExistingSignaturePathimg("0");
      setSignatureName("");

      // Reset file input
      if (signatureFileInputRef.current) {
        signatureFileInputRef.current.value = "";
      }

      Swal.fire({
        title: "موفق",
        text: "امضا با موفقیت حذف شد",
        icon: "success",
        confirmButtonText: "باشه",
      });
    } catch (error) {
      console.error("Error deleting signature:", error);
      Swal.fire({
        title: "خطا",
        text: error.response?.data?.message || "مشکلی در حذف امضا رخ داد",
        icon: "error",
        confirmButtonText: "باشه",
      });
    } finally {
      setUploadingSignature(false);
    }
  };

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (imageUrl && imageUrl.startsWith("blob:")) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const lastPointerPosition = useRef({ x: 0, y: 0 });
  const [selectedTextId, setSelectedTextId] = useState(null);

  // Initialize text elements with proper label logic
  const getTextForElement = (type, dateLabel, numLabel, attachLabel) => {
    if (type === "date") {
      const dateValue = formData.dateletter
        ? new Date(formData.dateletter).toLocaleDateString("fa-IR")
        : "";
      if (!dateValue) return "تاریخ";
      return dateLabel ? `تاریخ: ${dateValue}` : dateValue;
    } else if (type === "numletter") {
      const numValue = formData.numletter;
      const hasNumValue =
        numValue && typeof numValue === "string" && numValue.trim() !== "";
      if (!hasNumValue) return "شماره نامه";
      return numLabel ? `شماره: ${numValue}` : numValue;
    } else if (type === "attachment") {
      // Always use "دارد" as the value for attachment
      const attachmentValue = "دارد";
      return attachLabel ? `پیوست: ${attachmentValue}` : attachmentValue;
    }
    return "";
  };

  const getInitialTextElements = (dateLabel, numLabel, attachLabel) => {
    return {
      date: {
        text: getTextForElement("date", dateLabel, numLabel, attachLabel),
        x: formData.xdate || 70,
        y: formData.ydate || 75,
        fontSize: 10,
        fill: "#000000",
        fontFamily: "Vazirmatn",
      },
      numletter: {
        text: getTextForElement("numletter", dateLabel, numLabel, attachLabel),
        x: formData.xnumletter || 70,
        y: formData.ynumletter || 90,
        fontSize: 10,
        fill: "#000000",
        fontFamily: "Vazirmatn",
      },
      attachment: {
        text: getTextForElement("attachment", dateLabel, numLabel, attachLabel),
        x: formData.xatach || 70,
        y: formData.yatach || 105,
        fontSize: 10,
        fill: "#000000",
        fontFamily: "Vazirmatn",
      },
    };
  };

  // Text elements state - initialize with current label states
  const [textElements, setTextElements] = useState(() =>
    getInitialTextElements(
      formData.isdate !== undefined ? formData.isdate : false,
      formData.isnumletter !== undefined ? formData.isnumletter : false,
      formData.isattach !== undefined ? formData.isattach : false
    )
  );

  // Initialize stage size
  useEffect(() => {
    const updateStageSize = () => {
      if (containerRef.current) {
        const container = containerRef.current;
        const width = container.clientWidth;
        const height = container.clientHeight;

        setStageSize({ width, height });

        if (stageRef.current) {
          stageRef.current.width(width);
          stageRef.current.height(height);
          stageRef.current.batchDraw();
        }
      }
    };

    // Initial size update
    const timer = setTimeout(updateStageSize, 0);

    window.addEventListener("resize", updateStageSize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", updateStageSize);
    };
  }, []);

  // Reset initialization when imageUrl changes from API (not from file upload)
  useEffect(() => {
    if (imageUrl && !imageUrl.startsWith("blob:") && !image) {
      // Reset initialization when loading from API URL (not from file upload)
      // Only reset if image hasn't loaded yet
      setIsInitialized(false);
    }
  }, [imageUrl, image]);

  // Center image or white canvas on initial load
  useEffect(() => {
    if (stageSize.width > 0 && stageSize.height > 0 && !isInitialized) {
      const width = image ? image.width : A4_WIDTH;
      const height = image ? image.height : A4_HEIGHT;

      const initialScale = Math.min(
        (stageSize.width * 0.9) / width,
        (stageSize.height * 0.9) / height
      );

      // Zoom in a little (multiply by 1.2 for 20% zoom)
      const zoomedScale = initialScale * 1.1;

      const scaledWidth = width * zoomedScale;
      const scaledHeight = height * zoomedScale;

      setScale(zoomedScale);
      setPosition({
        x: (stageSize.width - scaledWidth) / 2,
        // Move up a bit (subtract 50px) so header is visible
        y: (stageSize.height - scaledHeight) / 2 + 55,
      });
      setIsInitialized(true);
    }
  }, [image, stageSize, isInitialized]);

  // Update text elements when formData or label states change (but preserve positions)
  useEffect(() => {
    // Generate date text
    const dateValue = formData.dateletter
      ? new Date(formData.dateletter).toLocaleDateString("fa-IR")
      : "";
    const dateText = dateValue
      ? isdate
        ? `تاریخ: ${dateValue}`
        : dateValue
      : "تاریخ";

    // Generate number text
    const numValue = formData.numletter || "۱۲";
    const hasNumValue =
      numValue && typeof numValue === "string" && numValue.trim() !== "";
    const numText = hasNumValue
      ? isnumletter
        ? `شماره: ${numValue}`
        : numValue
      : "شماره نامه";

    // Generate attachment text - always use "دارد" as the value
    const attachmentValue = "دارد";
    const attachText = isattach ? `پیوست: ${attachmentValue}` : attachmentValue;

    // Only update text content, preserve all positions from previous state
    // This ensures positions never reset when checkboxes toggle or formData changes
    setTextElements((prev) => ({
      date: {
        x: prev.date.x, // Always preserve current position
        y: prev.date.y, // Always preserve current position
        fontSize: prev.date.fontSize,
        fill: prev.date.fill,
        fontFamily: prev.date.fontFamily || "Vazirmatn",
        text: dateText, // Only update text
      },
      numletter: {
        x: prev.numletter.x, // Always preserve current position
        y: prev.numletter.y, // Always preserve current position
        fontSize: prev.numletter.fontSize,
        fill: prev.numletter.fill,
        fontFamily: prev.numletter.fontFamily || "Vazirmatn",
        text: numText, // Only update text
      },
      attachment: {
        x: prev.attachment.x, // Always preserve current position
        y: prev.attachment.y, // Always preserve current position
        fontSize: prev.attachment.fontSize,
        fill: prev.attachment.fill,
        fontFamily: prev.attachment.fontFamily || "Vazirmatn",
        text: attachText, // Only update text
      },
    }));
  }, [
    formData.dateletter,
    formData.numletter,
    formData.isattach,
    formData.attachmentNum,
    isdate,
    isnumletter,
    isattach,
  ]);

  const handleTextSelect = (id) => {
    setSelectedTextId(id);
  };

  const handleTextChange = (id, changes) => {
    setTextElements((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        ...changes,
      },
    }));
  };

  const handleTextDragEnd = (id, pos) => {
    handleTextChange(id, pos);
  };

  const handleSaveAndBack = async () => {
    // Convert normal (canvas) coordinates to modified (API) coordinates
    // normal data -> calculations -> modified data -> PUT to API
    const dateAPICoords = normalToModified(
      textElements.date.x,
      textElements.date.y
    );
    const numAPICoords = normalToModified(
      textElements.numletter.x,
      textElements.numletter.y
    );
    const attachAPICoords = normalToModified(
      textElements.attachment.x,
      textElements.attachment.y
    );

    // Update formData with normal (canvas) coordinates for UI use
    // Note: API receives modified coordinates via sarbargFormData below
    // UI always works with normal coordinates
    const updatedFormData = {
      ...formData,
      xdate: textElements.date.x, // Normal coordinates for UI
      ydate: textElements.date.y, // Normal coordinates for UI
      xnumletter: textElements.numletter.x, // Normal coordinates for UI
      ynumletter: textElements.numletter.y, // Normal coordinates for UI
      xatach: textElements.attachment.x, // Normal coordinates for UI
      yatach: textElements.attachment.y, // Normal coordinates for UI
      isdate: isdate,
      isnumletter: isnumletter,
      isattach: isattach,
      file: templateFile, // Include template file in formData
      // Include sarbarg (letterhead) IDs
      idsarbarg: existingTemplateId || 0,
      idsarbargstr: existingTemplateIdStr || "",
      // Include emza (signature) IDs
      idemza: existingSignatureId || 0,
      idemzastr: existingSignatureIdStr || "",
    };

    // Save/Update sarbarg with all the new fields
    try {
      setUploading(true);
      const sarbargFormData = new FormData();

      // Check if we have an existing template to update or need to create new one
      if (existingTemplateId || existingTemplateIdStr) {
        // EditSarbarg - include all fields
        sarbargFormData.append("idsarbarg", "0");
        sarbargFormData.append("idsarbargstr", existingTemplateIdStr || "");
        sarbargFormData.append(
          "idUser",
          currentUserId ? String(currentUserId) : "0"
        );
        sarbargFormData.append("name", templateName || "");
        sarbargFormData.append("pathimg", existingTemplatePathimg || "0");
        sarbargFormData.append("lang", "fa");
        sarbargFormData.append("isdate", isdate ? "true" : "false");
        sarbargFormData.append("isattach", isattach ? "true" : "false");
        sarbargFormData.append("isnumletter", isnumletter ? "true" : "false");
        sarbargFormData.append("xdate", String(dateAPICoords.x));
        sarbargFormData.append("ydate", String(dateAPICoords.y));
        sarbargFormData.append("xatach", String(attachAPICoords.x));
        sarbargFormData.append("yatach", String(attachAPICoords.y));
        sarbargFormData.append("xnumletter", String(numAPICoords.x));
        sarbargFormData.append("ynumletter", String(numAPICoords.y));

        // Include file only if templateFile exists
        if (templateFile) {
          sarbargFormData.append("file", templateFile);
        }

        const editResponse = await EditLetterHead(sarbargFormData);

        // Update pathimg from response if available
        if (editResponse.data?.pathimg) {
          setExistingTemplatePathimg(editResponse.data.pathimg);
        }

        Swal.fire({
          title: "موفق",
          text: "تنظیمات سربرگ با موفقیت به‌روزرسانی شد",
          icon: "success",
          confirmButtonText: "باشه",
        });
      } else {
        // AddSarbarg - include all fields
        sarbargFormData.append(
          "idUser",
          currentUserId ? String(currentUserId) : "0"
        );
        sarbargFormData.append("name", templateName || "");
        sarbargFormData.append("pathimg", "0");
        sarbargFormData.append("lang", "fa");
        sarbargFormData.append("isdate", isdate ? "true" : "false");
        sarbargFormData.append("isattach", isattach ? "true" : "false");
        sarbargFormData.append("isnumletter", isnumletter ? "true" : "false");
        sarbargFormData.append("xdate", String(dateAPICoords.x));
        sarbargFormData.append("ydate", String(dateAPICoords.y));
        sarbargFormData.append("xatach", String(attachAPICoords.x));
        sarbargFormData.append("yatach", String(attachAPICoords.y));
        sarbargFormData.append("xnumletter", String(numAPICoords.x));
        sarbargFormData.append("ynumletter", String(numAPICoords.y));

        // Include file only if templateFile exists
        if (templateFile) {
          sarbargFormData.append("file", templateFile);
        }

        const response = await AddLetterHead(sarbargFormData);

        // Get the IDs from response
        const newId = response.data?.id || response.data?.idsarbarg || 0;
        const newIdStr = response.data?.idsarbargstr || response.data?.id || "";

        if (newId) {
          setExistingTemplateId(newId);
        }
        if (newIdStr) {
          setExistingTemplateIdStr(newIdStr);
        }

        // Store pathimg from response if available
        if (response.data?.pathimg) {
          setExistingTemplatePathimg(response.data.pathimg);
        }

        Swal.fire({
          title: "موفق",
          text: "تنظیمات سربرگ با موفقیت ذخیره شد",
          icon: "success",
          confirmButtonText: "باشه",
        });
      }
    } catch (error) {
      console.error("Error saving sarbarg:", error);
      Swal.fire({
        title: "خطا",
        text:
          error.response?.data?.message ||
          "مشکلی در ذخیره تنظیمات سربرگ رخ داد",
        icon: "error",
        confirmButtonText: "باشه",
      });
    } finally {
      setUploading(false);
    }

    // Navigate back to form with updated data
    navigate("/addletter", {
      state: {
        formData: updatedFormData,
        templateFile: templateFile,
      },
    });
  };

  const handleWheel = (e) => {
    e.evt.preventDefault();

    const stage = stageRef.current;
    if (!stage) return;

    const oldScale = scale;
    const pointer = stage.getPointerPosition();
    if (!pointer) return;

    // Smooth zoom factor - smaller steps for smoother zooming
    const scaleBy = 1.05;
    const direction = e.evt.deltaY > 0 ? -1 : 1;

    // Calculate zoom based on scroll delta for smoother experience
    const delta = e.evt.deltaY;
    const zoomFactor = Math.abs(delta) > 100 ? 1.1 : 1.05;
    const newScale = Math.max(
      0.1,
      Math.min(5, oldScale * Math.pow(zoomFactor, direction))
    );

    // Calculate the point under the mouse in the image coordinates (before zoom)
    const mousePointTo = {
      x: (pointer.x - position.x) / oldScale,
      y: (pointer.y - position.y) / oldScale,
    };

    // Calculate new position to keep the point under the mouse in the same place
    const newPos = {
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    };

    setScale(newScale);
    setPosition(newPos);
  };

  const handleMouseDown = (e) => {
    // Deselect text when clicking on stage background or image
    const stage = stageRef.current;
    if (!stage) return;

    if (
      e.target === stage ||
      e.target.getType() === "Image" ||
      e.target.getType() === "Rect"
    ) {
      setSelectedTextId(null);
      setIsDragging(true);
      lastPointerPosition.current = stage.getPointerPosition();
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const stage = stageRef.current;
    if (!stage) return;

    const pointer = stage.getPointerPosition();
    if (!pointer) return;

    const dx = pointer.x - lastPointerPosition.current.x;
    const dy = pointer.y - lastPointerPosition.current.y;

    setPosition((prev) => ({
      x: prev.x + dx,
      y: prev.y + dy,
    }));

    lastPointerPosition.current = pointer;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#f5f5f5",
        p: { xs: 2, md: 3 },
      }}
    >
      {/* Header with Save Button */}
      <Paper
        elevation={2}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          p: { xs: 2, md: 3 },
          bgcolor: "white",
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "primary.main",
          }}
        >
          تنظیم موقعیت تاریخ، شماره و پیوست
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveAndBack}
          sx={{
            textTransform: "none",
            px: 2,
            py: 1,
            borderRadius: 2,
            fontSize: "1rem",
            fontWeight: 600,
            boxShadow: 2,
            "&:hover": {
              boxShadow: 4,
            },
          }}
        >
          ذخیره و بازگشت به فرم
        </Button>
      </Paper>
      <Paper
        elevation={3}
        sx={{
          p: 1,
          mb: 2,
          bgcolor: "white",
          borderRadius: 3,
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          border: "1px solid rgba(0,0,0,0.05)",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 1,
            fontWeight: 700,
            color: "text.primary",
          }}
        >
          تنظیمات نمایش برچسب‌ها
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 2,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={isdate}
                onChange={(e) => setIsdate(e.target.checked)}
                sx={{
                  "& .MuiSvgIcon-root": {
                    fontSize: 28,
                  },
                }}
              />
            }
            label={
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 500,
                  userSelect: "none",
                }}
              >
                نمایش برچسب 'تاریخ:' برای تاریخ
              </Typography>
            }
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: isdate ? "rgba(81, 44, 99, 0.1)" : "transparent",
              transition: "background-color 0.2s",
              "&:hover": {
                bgcolor: isdate ? "rgba(81, 44, 99, 0.15)" : "grey.50",
              },
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={isnumletter}
                onChange={(e) => setIsnumletter(e.target.checked)}
                sx={{
                  "& .MuiSvgIcon-root": {
                    fontSize: 28,
                  },
                }}
              />
            }
            label={
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 500,
                  userSelect: "none",
                }}
              >
                نمایش برچسب 'شماره:' برای شماره نامه
              </Typography>
            }
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: isnumletter ? "rgba(81, 44, 99, 0.1)" : "transparent",
              transition: "background-color 0.2s",
              "&:hover": {
                bgcolor: isnumletter ? "rgba(81, 44, 99, 0.15)" : "grey.50",
              },
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={isattach}
                onChange={(e) => setIsattach(e.target.checked)}
                sx={{
                  "& .MuiSvgIcon-root": {
                    fontSize: 28,
                  },
                }}
              />
            }
            label={
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 500,
                  userSelect: "none",
                }}
              >
                نمایش برچسب 'پیوست:' برای پیوست
              </Typography>
            }
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: isattach ? "rgba(81, 44, 99, 0.1)" : "transparent",
              transition: "background-color 0.2s",
              "&:hover": {
                bgcolor: isattach ? "rgba(81, 44, 99, 0.15)" : "grey.50",
              },
            }}
          />
        </Box>
      </Paper>
      {/* Main Content Area */}
      <Box
        sx={{
          display: "flex",
          gap: 3,
          flexDirection: { xs: "column", lg: "row" },
          flex: 1,
          minHeight: 0,
          alignItems: { xs: "stretch", lg: "flex-start" },
        }}
      >
        {/* Left Sidebar - Controls */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: { xs: "100%", lg: "350px" },
            minWidth: { xs: "100%", lg: "320px" },
          }}
        >
          {/* Template Upload/Edit Section */}
          <Paper
            elevation={3}
            sx={{
              p: 3,
              mb: 2,
              bgcolor: "white",
              borderRadius: 3,
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              border: "1px solid rgba(0,0,0,0.05)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2.5,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                }}
              >
                مدیریت سربرگ
              </Typography>
              {(existingTemplateId || existingTemplateIdStr) && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleEditTemplate}
                  disabled={uploading || loadingTemplate}
                  sx={{ textTransform: "none" }}
                >
                  ویرایش نام
                </Button>
              )}
            </Box>
            {loadingTemplate ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                <TextField
                  label="نام سربرگ"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="نام سربرگ را وارد کنید"
                  disabled={uploading}
                />
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Button
                    variant="outlined"
                    component="label"
                    disabled={uploading}
                    sx={{
                      textTransform: "none",
                      px: 3,
                      py: 1,
                      borderRadius: 2,
                      fontWeight: 600,
                    }}
                  >
                    {existingTemplateId || existingTemplateIdStr
                      ? "تغییر فایل"
                      : "انتخاب فایل"}
                    <input
                      ref={fileInputRef}
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleFileChange}
                      disabled={uploading}
                    />
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleConfirmTemplate}
                    disabled={
                      uploading ||
                      !templateName?.trim() ||
                      (!templateFile &&
                        !existingTemplateId &&
                        !existingTemplateIdStr)
                    }
                    sx={{
                      textTransform: "none",
                      px: 3,
                      py: 1,
                      borderRadius: 2,
                      fontWeight: 600,
                      boxShadow: 2,
                      "&:hover": {
                        boxShadow: 4,
                      },
                    }}
                  >
                    {uploading ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        در حال ذخیره...
                      </>
                    ) : existingTemplateId || existingTemplateIdStr ? (
                      "ذخیره تغییرات"
                    ) : (
                      "تأیید و ذخیره"
                    )}
                  </Button>
                  {(templateFile ||
                    existingTemplateId ||
                    existingTemplateIdStr) && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleRemoveTemplate}
                      disabled={uploading}
                      sx={{
                        textTransform: "none",
                        px: 3,
                        py: 1,
                        borderRadius: 2,
                        fontWeight: 600,
                        borderWidth: 2,
                        "&:hover": {
                          borderWidth: 2,
                          bgcolor: "rgba(200, 29, 17, 0.08)",
                        },
                      }}
                    >
                      حذف قالب
                    </Button>
                  )}
                </Box>
                {templateFile && (
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "success.lighter",
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "success.main",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "success.dark",
                        fontWeight: 500,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      قالب انتخاب شده: {templateFile.name}
                    </Typography>
                  </Box>
                )}
                {!templateFile && !loadingTemplate && (
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "grey.50",
                      borderRadius: 2,
                      border: "1px dashed",
                      borderColor: "grey.300",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        lineHeight: 1.8,
                      }}
                    >
                      در صورت عدم انتخاب قالب، از کاغذ سفید A4 استفاده می‌شود
                    </Typography>
                  </Box>
                )}
                <Alert severity="warning">
                  فقط فایل‌های تصویری قابل آپلود هستند
                </Alert>
              </Box>
            )}
          </Paper>

          {/* Signature Upload/Edit Section */}
          <Paper
            elevation={3}
            sx={{
              p: 3,
              mb: 2,
              bgcolor: "white",
              borderRadius: 3,
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              border: "1px solid rgba(0,0,0,0.05)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2.5,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                }}
              >
                مدیریت امضا
              </Typography>
              {(existingSignatureId || existingSignatureIdStr) && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleEditSignature}
                  disabled={uploadingSignature || loadingSignature}
                  sx={{ textTransform: "none" }}
                >
                  ویرایش نام
                </Button>
              )}
            </Box>
            {loadingSignature ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                <TextField
                  label="نام امضا"
                  value={signatureName}
                  onChange={(e) => setSignatureName(e.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="نام امضا را وارد کنید"
                  disabled={uploadingSignature}
                />
                {/* Display existing signature image */}
                {existingSignaturePathimg &&
                  existingSignaturePathimg !== "0" && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        p: 2,
                        bgcolor: "grey.50",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "text.secondary",
                          mb: 0.5,
                        }}
                      >
                        پیش‌نمایش امضا:
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          bgcolor: "white",
                          borderRadius: 1,
                          p: 2,
                          border: "1px solid",

                          borderColor: "divider",
                        }}
                      >
                        <img
                          src={existingSignaturePathimg}
                          alt="امضا"
                          style={{
                            width: "50%",

                            objectFit: "contain",
                          }}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Button
                    variant="outlined"
                    component="label"
                    disabled={uploadingSignature}
                    sx={{
                      textTransform: "none",
                      px: 3,
                      py: 1,
                      borderRadius: 2,
                      fontWeight: 600,
                    }}
                  >
                    {existingSignatureId || existingSignatureIdStr
                      ? "تغییر فایل"
                      : "انتخاب فایل"}
                    <input
                      ref={signatureFileInputRef}
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleSignatureFileChange}
                      disabled={uploadingSignature}
                    />
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleConfirmSignature}
                    disabled={
                      uploadingSignature ||
                      !signatureName?.trim() ||
                      (!signatureFile &&
                        !existingSignatureId &&
                        !existingSignatureIdStr)
                    }
                    sx={{
                      textTransform: "none",
                      px: 3,
                      py: 1,
                      borderRadius: 2,
                      fontWeight: 600,
                      boxShadow: 2,
                      "&:hover": {
                        boxShadow: 4,
                      },
                    }}
                  >
                    {uploadingSignature ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        در حال ذخیره...
                      </>
                    ) : existingSignatureId || existingSignatureIdStr ? (
                      "ذخیره تغییرات"
                    ) : (
                      "تأیید و ذخیره"
                    )}
                  </Button>
                  {(signatureFile ||
                    existingSignatureId ||
                    existingSignatureIdStr) && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleRemoveSignature}
                      disabled={uploadingSignature}
                      sx={{
                        textTransform: "none",
                        px: 3,
                        py: 1,
                        borderRadius: 2,
                        fontWeight: 600,
                        borderWidth: 2,
                        "&:hover": {
                          borderWidth: 2,
                          bgcolor: "rgba(200, 29, 17, 0.08)",
                        },
                      }}
                    >
                      حذف امضا
                    </Button>
                  )}
                </Box>
                {signatureFile && (
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "success.lighter",
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "success.main",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "success.dark",
                        fontWeight: 500,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      امضا انتخاب شده: {signatureFile.name}
                    </Typography>
                  </Box>
                )}

                <Alert severity="warning">
                  فقط فایل‌های تصویری قابل آپلود هستند
                </Alert>
              </Box>
            )}
          </Paper>

          {/* Label Checkboxes Section */}
        </Box>

        {/* Right Side - Canvas Container */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: "1050px",
            minWidth: 0,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 2,
              bgcolor: "white",
              borderRadius: 3,
              
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              border: "1px solid rgba(0,0,0,0.05)",
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box
              ref={containerRef}
              sx={{
                width: "100%",
                height: "100%",
                maxWidth: "900px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                cursor: "default",
                position: "relative",
                borderRadius: 2,
                border: "2px solid",
                borderColor: "grey.300",
                bgcolor: "grey.100",
                boxShadow: "inset 0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <Stage
                width={stageSize.width || window.innerWidth}
                height={stageSize.height || window.innerHeight * 1}
                ref={stageRef}
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                draggable={false}
                style={{ cursor: isDragging ? "grabbing" : "grab" }}
              >
                <Layer
                  scaleX={scale}
                  scaleY={scale}
                  x={position.x}
                  y={position.y}
                  draggable={false}
                >
                  <BackgroundImage
                    image={image}
                    showWhiteCanvas={showWhiteCanvas}
                  />

                  {/* Text Elements - Always visible, labels controlled by checkboxes */}
                  {Object.entries(textElements).map(([id, textData]) => (
                    <EditableText
                      key={id}
                      text={textData.text}
                      x={textData.x}
                      y={textData.y}
                      fontSize={textData.fontSize}
                      fill={textData.fill}
                      fontFamily={textData.fontFamily || "Vazirmatn"}
                      isSelected={selectedTextId === id}
                      onSelect={() => handleTextSelect(id)}
                      onDragEnd={(pos) => handleTextDragEnd(id, pos)}
                    />
                  ))}
                </Layer>
              </Stage>
            </Box>
          </Paper>
        </Box>
      </Box>
      {/* Edit Template Name Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>ویرایش نام قالب</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="نام قالب"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              fullWidth
              variant="outlined"
              placeholder="نام قالب را وارد کنید"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={uploading}>
            انصراف
          </Button>
          <Button
            onClick={handleSaveTemplateName}
            variant="contained"
            disabled={uploading || !templateName.trim()}
            sx={{ textTransform: "none" }}
          >
            {uploading ? <CircularProgress size={24} /> : "ذخیره"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Signature Edit Dialog */}
      <Dialog
        open={editSignatureDialogOpen}
        onClose={() => setEditSignatureDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>ویرایش نام امضا</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="نام امضا"
              value={signatureName}
              onChange={(e) => setSignatureName(e.target.value)}
              fullWidth
              variant="outlined"
              placeholder="نام امضا را وارد کنید"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEditSignatureDialogOpen(false)}
            disabled={uploadingSignature}
          >
            انصراف
          </Button>
          <Button
            onClick={handleSaveSignatureName}
            variant="contained"
            disabled={uploadingSignature || !signatureName.trim()}
            sx={{ textTransform: "none" }}
          >
            {uploadingSignature ? <CircularProgress size={24} /> : "ذخیره"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Adjustment;
